import React from 'react';
import { Container, Typography, Link, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * IncidentLocationMap — shows the reported incident location on a map (FEAT-014 portal side).
 *
 * The mobile app captures location as:
 *   { source: "gps" | "wifi" | "exif" | "none", latitude, longitude, accuracy, bssid, ssid, capturedAt }
 *   ("exif" = derived from an attached photo's GPS tags — FEAT-016; an indoor fallback for FEAT-014.)
 *
 * Rendering strategy (no API key / billing required):
 *  - GPS with lat/lng  -> interactive Google Maps embed (iframe) centered on the point, plus
 *    "Open in Google Maps" / "Open in Apple Maps" deep links for responders on the go.
 *  - Wi-Fi only (BSSID, no resolved coords) -> show the network info; we can't plot a pin until
 *    the BSSID is resolved to a location (district AP->location map, FEAT-014 backend).
 *  - No location -> render nothing.
 *
 * NOTE: uses the keyless Google Maps `output=embed` iframe. To swap to the Maps JavaScript API
 * (custom markers/styling) later, replace the iframe with a map component + a restricted API key.
 */
// lat/lng may arrive as numbers OR strings (the backend's DecimalEncoder serializes DynamoDB
// Decimals to strings), so coerce defensively.
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};
const hasCoords = (loc) => {
  if (!loc) return false;
  const lat = toNum(loc.latitude);
  const lng = toNum(loc.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) && !(lat === 0 && lng === 0);
};

const IncidentLocationMap = ({ location }) => {
  const theme = useTheme();

  if (!location || location.source === 'none' || (!hasCoords(location) && !location.bssid)) {
    return null;
  }

  const style = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '60px',
      marginRight: '60px',
      marginTop: '40px',
      width: 'auto',
      paddingLeft: '0px !important',
      border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`,
      borderRadius: '4px',
    },
    header: { borderBottom: `1px solid ${theme.palette.custom?.gray || '#ccc'}` },
    title: {
      fontSize: '20px',
      fontWeight: '700',
      marginTop: '20px',
      marginBottom: '20px',
      marginLeft: '20px',
    },
    body: { padding: '20px' },
    meta: { fontSize: '13px', color: theme.palette.text?.secondary || '#666', marginBottom: '12px' },
    mapFrame: { width: '100%', height: '360px', border: 0, borderRadius: '8px' },
    links: { marginTop: '12px', display: 'flex', gap: '20px' },
  };

  if (hasCoords(location)) {
    const lat = toNum(location.latitude);
    const lng = toNum(location.longitude);
    const { accuracy, capturedAt } = location;
    const q = `${lat},${lng}`;
    const embedSrc = `https://www.google.com/maps?q=${q}&z=17&output=embed`;
    const googleLink = `https://www.google.com/maps/search/?api=1&query=${q}`;
    const appleLink = `https://maps.apple.com/?q=${q}`;

    return (
      <Container sx={style.container}>
        <Container sx={style.header}>
          <Typography sx={style.title}>Reported Location</Typography>
        </Container>
        <Box sx={style.body}>
          <Typography sx={style.meta}>
            Source: {location.source === 'wifi' ? 'Wi-Fi (approximate)' : location.source === 'exif' ? 'Photo (EXIF)' : 'GPS'}
            {!Number.isNaN(toNum(accuracy)) ? ` · accuracy ~${Math.round(toNum(accuracy))}m` : ''}
            {capturedAt ? ` · captured ${new Date(capturedAt).toLocaleString()}` : ''}
          </Typography>
          <iframe
            title="Incident location map"
            src={embedSrc}
            style={style.mapFrame}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          <Box sx={style.links}>
            <Link href={googleLink} target="_blank" rel="noopener noreferrer">Open in Google Maps</Link>
            <Link href={appleLink} target="_blank" rel="noopener noreferrer">Open in Apple Maps</Link>
          </Box>
        </Box>
      </Container>
    );
  }

  // Wi-Fi-only (no resolved coordinates yet).
  return (
    <Container sx={style.container}>
      <Container sx={style.header}>
        <Typography sx={style.title}>Reported Location</Typography>
      </Container>
      <Box sx={style.body}>
        <Typography sx={style.meta}>
          Approximate location via the connected Wi-Fi network. A precise map isn't available
          until this network is mapped to a location.
        </Typography>
        {location.ssid ? <Typography sx={style.meta}>Network: {location.ssid}</Typography> : null}
        {location.bssid ? <Typography sx={style.meta}>BSSID: {location.bssid}</Typography> : null}
      </Box>
    </Container>
  );
};

export default IncidentLocationMap;
